import { Header, StatsCard, TableCard } from "@general-backoffice/core";
import type { PaginationMeta } from "@investhinks/api-client";
import { type TFunction } from "i18next";
import moment from "moment";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, Col, Container, Row } from "reactstrap";

import { adminStatsEndpoints } from "../../../../../api";
import DailySignUpsGraphCard from "../../../../../components/adminStats/DailySignUpsGraphCard";
import YearlySignUpsGraph from "../../../../../components/adminStats/graphs/YearlySignUpsGraph";
import YearMonthSelector from "../../../../../components/adminStats/YearMonthSelector";
import usePagination from "../../../../../components/hooks/usePagination";
import PaginatedTableFooter from "../../../../../components/shared/PaginatedTableFooter";
import MapGraph, { type MapDataItem } from "../../../../../components/stats/MapGraph";
import useFetch from "../../../../../hooks/useFetch";
import { getCountries } from "../../../../../utils/getCountries";
import { TypedTable } from "../../../../../utils/typedLib";

// Local type definitions
interface CountrySignUpStats {
  nonClients?: number;
  clients?: number;
  notVerified?: number;
  total?: number;
}

interface SignUpPerCountry extends CountrySignUpStats {
  countryCode: string;
}

interface SignUpsPerCountryProps {
  signUpsPerCountry: SignUpPerCountry[];
  isLoading: boolean;
  pagination?: PaginationMeta | null;
  goToPage?: (page: number) => void;
  changePageSize?: (size: number) => void;
  t: TFunction;
}

const SignUps: React.FC = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "adminPages.signUps" });
  const [date, setDate] = useState(moment());

  const { state: usersPerCountryResponse, isLoading: isLoadingUsersPerCountry } = useFetch(
    () =>
      adminStatsEndpoints
        .getUsersPerCountry({
          filters: {
            createdAt: {
              $gte: date.startOf("M").toISOString(),
              $lte: date.endOf("M").toISOString(),
            },
          },
        })
        .then(({ data }) => data),
    { deps: [date] }
  );
  // getUsersPerCountry returns data directly (not a tuple)
  const { perCountry: usersPerCountry = {} } = usersPerCountryResponse || {};

  const usersPerCountryTyped: Record<string, CountrySignUpStats> = usersPerCountry;
  const signedUsersPerCountry: SignUpPerCountry[] = useMemo(
    () => Object.entries(usersPerCountryTyped).map(([key, values]) => ({ countryCode: key, ...values })),
    [usersPerCountryTyped]
  );

  const totalVerifiedUsers = useMemo(
    () =>
      signedUsersPerCountry.reduce(
        (r: number, { nonClients, clients }: SignUpPerCountry) => r + (nonClients || 0) + (clients || 0),
        0
      ),
    [signedUsersPerCountry]
  );

  const { pageItems: pageUsersPerCountry, ...usersPerCountryPaginationProps } = usePagination(signedUsersPerCountry);

  const { state: dailySignUpsResponse, isLoading: isLoadingDailySignUps } = useFetch(
    () => adminStatsEndpoints.getMonthDailySignUps(date.format("YYYY-MM")).then(({ data }) => data),
    { deps: [date] }
  );
  // getMonthDailySignUps returns { total, perDay } - perDay is an object keyed by day number
  const { perDay: dailySignUps = {} } = dailySignUpsResponse || {};

  const currentYear = date.year();
  const previousYear = currentYear - 1;
  const twoYearsAgo = currentYear - 2;

  const { state: currentYearSignUps, isLoading: isLoadingCurrentYear } = useFetch(
    () => adminStatsEndpoints.getYearMonthlySignUps(currentYear).then(({ data }) => data),
    { deps: [currentYear] }
  );

  const { state: previousYearSignUps, isLoading: isLoadingPreviousYear } = useFetch(
    () => adminStatsEndpoints.getYearMonthlySignUps(previousYear).then(({ data }) => data),
    { deps: [previousYear] }
  );

  const { state: twoYearsAgoSignUps, isLoading: isLoadingTwoYearsAgo } = useFetch(
    () => adminStatsEndpoints.getYearMonthlySignUps(twoYearsAgo).then(({ data }) => data),
    { deps: [twoYearsAgo] }
  );

  return (
    <React.Fragment>
      <Header.BreadCrumb color="secondary" isDark={false}>
        <div className="position-absolute mt-3 top-3 right-4">
          <YearMonthSelector date={date} setDate={setDate} />
        </div>

        <Col xs={12}>
          <Card body className="p-0">
            <MapGraph
              data={signedUsersPerCountry.map(
                ({ countryCode, total }): MapDataItem => ({
                  id: countryCode,
                  value: total,
                })
              )}
              isLoading={isLoadingUsersPerCountry}
              className=""
              style={{}}
            />
          </Card>
        </Col>
      </Header.BreadCrumb>
      <Container className="mt--6" fluid>
        <Row>
          <Col xs={12} xl={5}>
            <StatsCard.Number
              color="success"
              isDark={true}
              number={totalVerifiedUsers}
              suffix={t("signUps")}
              title={t("totalSignUps", { date: date.format("MMMM YYYY") })}
              description={t("excludingUnverified")}
              icon="fas fa-users"
            />
            <SignUpsPerCountry
              {...usersPerCountryPaginationProps}
              signUpsPerCountry={pageUsersPerCountry}
              isLoading={isLoadingUsersPerCountry}
              t={t}
            />
          </Col>
          <Col xs={12} xl={7}>
            <DailySignUpsGraphCard data={dailySignUps} date={date} isLoading={isLoadingDailySignUps} />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col>
            <h4>{t("yearOverYear")}</h4>
            <YearlySignUpsGraph
              currentYear={currentYear}
              previousYear={previousYear}
              twoYearsAgo={twoYearsAgo}
              currentYearData={currentYearSignUps ?? null}
              previousYearData={previousYearSignUps ?? null}
              twoYearsAgoData={twoYearsAgoSignUps ?? null}
              isLoading={isLoadingCurrentYear || isLoadingPreviousYear || isLoadingTwoYearsAgo}
            />
          </Col>
        </Row>
      </Container>
    </React.Fragment>
  );
};

const countries: Record<string, string> = getCountries("en");
const SignUpsPerCountry: React.FC<SignUpsPerCountryProps> = ({
  signUpsPerCountry,
  isLoading,
  pagination,
  goToPage,
  changePageSize,
  t,
}) => {
  const columns = [
    {
      compressed: true,
      name: t("columns.country"),
      formattedContent: ({ countryCode }: SignUpPerCountry) => countries?.[countryCode] || countryCode,
      isTh: true,
    },
    {
      name: t("columns.total"),
      compressed: true,
      property: "total",
      isTh: true,
    },
    {
      name: t("columns.clients"),
      compressed: true,
      property: "clients",
      isTh: true,
    },
    {
      name: t("columns.nonClients"),
      compressed: true,
      property: "nonClients",
      isTh: true,
    },
    {
      name: t("columns.notVerified"),
      compressed: true,
      property: "notVerified",
      isTh: true,
    },
  ];

  return (
    <Card className="overflow-hidden">
      <TableCard.Header title={t("perCountryTitle")} />
      <TypedTable
        columns={columns}
        rows={signUpsPerCountry}
        isLoading={isLoading}
        noContentMessage={t("noSignUps")}
        rowAmount={pagination?.pageSize}
        selectable={false}
        sortable={false}
      />
      {pagination && goToPage && changePageSize && (
        <PaginatedTableFooter
          pagination={pagination}
          isLoading={isLoading}
          goToPage={goToPage}
          changePageSize={changePageSize}
          amountOptions={[10, 25, 50]}
        />
      )}
    </Card>
  );
};

export default SignUps;
